import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartPie,
  faEnvelopeOpenText,
  faPaperPlane,
  faCircleCheck,
  faTriangleExclamation,
  faClock,
  faEye,
  faChartColumn,
} from '@fortawesome/free-solid-svg-icons';
import { getMessageStats, getMyInfo } from '../../../lib/api';
import '../../dashboard/styles/dashboard.css';
import '../../admin/pages/admin.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

const isoDate = (d) => d.toISOString().slice(0, 10);

const PERIOD_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'this_month', label: 'This month' },
  { value: 'overall', label: 'Overall' },
  { value: 'custom', label: 'Custom range' },
];

const ACCENT = '#00a884';
const CHART_MUTED = 'rgba(100, 116, 139, 0.35)';

const formatInt = (v) => {
  const n = Number(v);
  if (Number.isNaN(n) || v === '' || v == null) return '—';
  return new Intl.NumberFormat('en-IN').format(n);
};

const pickNum = (row, snakeKey) => {
  const camel = snakeKey.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
  const raw = row?.[snakeKey] ?? row?.[camel];
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
};

const doughnutSafe = (a, b) => {
  const x = Math.max(0, a);
  const y = Math.max(0, b);
  if (x === 0 && y === 0) return [1, 0];
  return [x, y];
};

const OverviewStatCard = ({ title, value, subtitle, segments }) => (
  <div className="dashboard-stat-card">
    <div className="dashboard-stat-content">
      <div className="dashboard-stat-text">
        <p className="dashboard-stat-title">{title}</p>
        <h2 className="dashboard-stat-value">{value}</h2>
        <p className="dashboard-stat-trend">{subtitle}</p>
      </div>
      <div className="dashboard-stat-chart">
        <Doughnut
          data={{
            labels: ['', ''],
            datasets: [
              {
                data: segments,
                backgroundColor: ['rgba(255,255,255,0.92)', 'rgba(255,255,255,0.22)'],
                borderWidth: 0,
              },
            ],
          }}
          options={{
            maintainAspectRatio: false,
            cutout: '70%',
            plugins: { legend: { display: false }, tooltip: { enabled: false } },
          }}
        />
      </div>
    </div>
  </div>
);

const UserOverview = () => {
  const today = useMemo(() => new Date(), []);
  const defaultFrom = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return d;
  }, []);

  const [period, setPeriod] = useState('today');
  const [fromDate, setFromDate] = useState(() => isoDate(defaultFrom));
  const [toDate, setToDate] = useState(() => isoDate(today));
  const [appliedCustomRange, setAppliedCustomRange] = useState(null);
  const [stats, setStats] = useState(null);
  const [profile, setProfile] = useState({ name: '', email: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getMyInfo()
      .then((data) => {
        setProfile({
          name: data?.userInfo?.name || '',
          email: data?.userInfo?.email || '',
        });
      })
      .catch(() => {});
  }, []);

  const sums = useMemo(
    () => ({
      total: pickNum(stats, 'total'),
      read: pickNum(stats, 'read'),
      sent: pickNum(stats, 'sent'),
      delivered: pickNum(stats, 'delivered'),
      failed: pickNum(stats, 'failed'),
      pending: pickNum(stats, 'pending'),
      delivery_failed: pickNum(stats, 'delivery_failed'),
    }),
    [stats]
  );

  const pipeline = sums.sent + sums.pending;
  const failures = sums.failed + sums.delivery_failed;

  const statusDoughnutData = useMemo(() => {
    const parts = [
      { key: 'read', label: 'Read', v: sums.read, color: '#38bdf8' },
      { key: 'sent', label: 'Sent', v: sums.sent, color: '#a78bfa' },
      { key: 'delivered', label: 'Delivered', v: sums.delivered, color: ACCENT },
      { key: 'failed', label: 'Failed', v: sums.failed, color: '#f87171' },
      { key: 'pending', label: 'Pending', v: sums.pending, color: '#fbbf24' },
      { key: 'delivery_failed', label: 'Delivery failed', v: sums.delivery_failed, color: '#fb7185' },
    ].filter((p) => p.v > 0);
    if (!parts.length) {
      return {
        labels: ['No data'],
        datasets: [{ data: [1], backgroundColor: [CHART_MUTED], borderWidth: 0 }],
      };
    }
    return {
      labels: parts.map((p) => p.label),
      datasets: [
        {
          data: parts.map((p) => p.v),
          backgroundColor: parts.map((p) => p.color),
          borderWidth: 0,
        },
      ],
    };
  }, [sums]);

  const statusBarData = useMemo(
    () => ({
      labels: ['Read', 'Sent', 'Delivered', 'Failed', 'Pending', 'Del. failed'],
      datasets: [
        {
          label: 'Count',
          data: [sums.read, sums.sent, sums.delivered, sums.failed, sums.pending, sums.delivery_failed],
          backgroundColor: ['#38bdf8', '#a78bfa', ACCENT, '#f87171', '#fbbf24', '#fb7185'],
          borderRadius: 6,
          borderWidth: 0,
        },
      ],
    }),
    [sums]
  );

  const barOptionsVertical = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => formatInt(ctx.raw),
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: '#64748b', font: { size: 11 } },
        },
        y: {
          beginAtZero: true,
          grid: { color: CHART_MUTED },
          ticks: { color: '#64748b', maxTicksLimit: 8 },
        },
      },
    }),
    []
  );

  const doughnutStatusOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: { color: '#64748b', boxWidth: 10, padding: 12, font: { size: 11 } },
        },
        tooltip: {
          callbacks: {
            label: (ctx) => ` ${ctx.label}: ${formatInt(ctx.raw)}`,
          },
        },
      },
    }),
    []
  );

  const fetchStats = useCallback(async (nextPeriod, customRange) => {
    setError('');
    setLoading(true);
    try {
      const params = { period: nextPeriod };
      if (nextPeriod === 'custom') {
        const range = customRange || appliedCustomRange;
        if (!range?.from || !range?.to) {
          setLoading(false);
          return;
        }
        params.fromDate = range.from;
        params.toDate = range.to;
      }
      const data = await getMessageStats(params);
      setStats(data);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load message stats');
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [appliedCustomRange]);

  useEffect(() => {
    if (period === 'custom') return;
    setAppliedCustomRange(null);
    fetchStats(period);
  }, [period, fetchStats]);

  const onPeriodChange = (e) => {
    const next = e.target.value;
    setPeriod(next);
    if (next === 'custom') {
      setStats(null);
      setAppliedCustomRange(null);
    }
  };

  const applyCustomRange = () => {
    if (!fromDate || !toDate) {
      setError('Select both start and end dates.');
      return;
    }
    if (fromDate > toDate) {
      setError('Start date cannot be after end date.');
      return;
    }
    const range = { from: fromDate, to: toDate };
    setAppliedCustomRange(range);
    fetchStats('custom', range);
  };

  const periodLabel = PERIOD_OPTIONS.find((o) => o.value === period)?.label || period;

  return (
    <div className="admin-page">
      <header className="admin-page__hero">
        <div className="admin-page__title-row">
          <div className="admin-page__icon" aria-hidden>
            <FontAwesomeIcon icon={faChartPie} />
          </div>
          <div>
            <span className="admin-page__eyebrow">Reports</span>
            <h1 className="admin-page__title">Overview</h1>
            <p className="admin-page__subtitle">
              Your message delivery stats for the selected period — same filters as the admin overview,
              scoped to your account only.
            </p>
          </div>
        </div>
      </header>

      <section className="admin-panel admin-overview-filters">
        <div className="admin-panel__body">
          {error && (
            <div className="admin-feedback admin-feedback--error" style={{ marginBottom: 16 }}>
              {error}
            </div>
          )}
          <div className="admin-field" style={{ marginBottom: period === 'custom' ? 0 : undefined }}>
            <label className="admin-label" htmlFor="user-overview-period">
              Period
            </label>
            <select
              id="user-overview-period"
              className="admin-select"
              value={period}
              onChange={onPeriodChange}
            >
              {PERIOD_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          {period === 'custom' && (
            <div className="admin-grid admin-message-stats-range" style={{ marginTop: 16 }}>
              <div className="admin-field">
                <label className="admin-label" htmlFor="user-overview-from">
                  From
                </label>
                <input
                  id="user-overview-from"
                  type="date"
                  className="admin-input"
                  value={fromDate}
                  max={toDate || undefined}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>
              <div className="admin-field">
                <label className="admin-label" htmlFor="user-overview-to">
                  To
                </label>
                <input
                  id="user-overview-to"
                  type="date"
                  className="admin-input"
                  value={toDate}
                  min={fromDate || undefined}
                  max={isoDate(today)}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>
              <div className="admin-field admin-field--actions">
                <span className="admin-label" aria-hidden="true">
                  &nbsp;
                </span>
                <button
                  type="button"
                  className="admin-btn admin-btn--primary"
                  disabled={loading}
                  onClick={applyCustomRange}
                >
                  Apply range
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      <div className="admin-overview-dash dashboard-wrap">
        <div className="dashboard-info-bar">
          <div className="dashboard-info-header">
            <span className="dashboard-info-title">Period totals · {periodLabel}</span>
          </div>
          <div className="dashboard-info-grid admin-overview-kpi-grid">
            <div className="dashboard-info-item dashboard-info-item--highlight">
              <span className="dashboard-info-icon">
                <FontAwesomeIcon icon={faChartColumn} />
              </span>
              <div className="dashboard-info-meta">
                <span className="dashboard-info-label">Total messages</span>
                <span className="dashboard-info-value">{loading ? '…' : formatInt(sums.total)}</span>
              </div>
            </div>
            <div className="dashboard-info-item">
              <span className="dashboard-info-icon">
                <FontAwesomeIcon icon={faEye} />
              </span>
              <div className="dashboard-info-meta">
                <span className="dashboard-info-label">Read</span>
                <span className="dashboard-info-value">{loading ? '…' : formatInt(sums.read)}</span>
              </div>
            </div>
            <div className="dashboard-info-item">
              <span className="dashboard-info-icon">
                <FontAwesomeIcon icon={faPaperPlane} />
              </span>
              <div className="dashboard-info-meta">
                <span className="dashboard-info-label">Sent</span>
                <span className="dashboard-info-value">{loading ? '…' : formatInt(sums.sent)}</span>
              </div>
            </div>
            <div className="dashboard-info-item">
              <span className="dashboard-info-icon">
                <FontAwesomeIcon icon={faCircleCheck} />
              </span>
              <div className="dashboard-info-meta">
                <span className="dashboard-info-label">Delivered</span>
                <span className="dashboard-info-value">{loading ? '…' : formatInt(sums.delivered)}</span>
              </div>
            </div>
            <div className="dashboard-info-item">
              <span className="dashboard-info-icon">
                <FontAwesomeIcon icon={faTriangleExclamation} />
              </span>
              <div className="dashboard-info-meta">
                <span className="dashboard-info-label">Failed</span>
                <span className="dashboard-info-value">{loading ? '…' : formatInt(sums.failed)}</span>
              </div>
            </div>
            <div className="dashboard-info-item">
              <span className="dashboard-info-icon">
                <FontAwesomeIcon icon={faClock} />
              </span>
              <div className="dashboard-info-meta">
                <span className="dashboard-info-label">Pending</span>
                <span className="dashboard-info-value">{loading ? '…' : formatInt(sums.pending)}</span>
              </div>
            </div>
            <div className="dashboard-info-item">
              <span className="dashboard-info-icon">
                <FontAwesomeIcon icon={faEnvelopeOpenText} />
              </span>
              <div className="dashboard-info-meta">
                <span className="dashboard-info-label">Delivery failed</span>
                <span className="dashboard-info-value">{loading ? '…' : formatInt(sums.delivery_failed)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-cards-row">
          <OverviewStatCard
            title="Total volume"
            value={loading ? '…' : formatInt(sums.total)}
            subtitle="Delivered vs rest"
            segments={doughnutSafe(sums.delivered, Math.max(0, sums.total - sums.delivered))}
          />
          <OverviewStatCard
            title="Read"
            value={loading ? '…' : formatInt(sums.read)}
            subtitle="Share of all messages"
            segments={doughnutSafe(sums.read, Math.max(0, sums.total - sums.read))}
          />
          <OverviewStatCard
            title="Sent + pending"
            value={loading ? '…' : formatInt(pipeline)}
            subtitle="In flight vs rest"
            segments={doughnutSafe(pipeline, Math.max(0, sums.total - pipeline))}
          />
          <OverviewStatCard
            title="Failures"
            value={loading ? '…' : formatInt(failures)}
            subtitle="Failed + delivery failed"
            segments={doughnutSafe(failures, Math.max(0, sums.total - failures))}
          />
        </div>

        <div className="dashboard-charts-row">
          <div className="dashboard-chart-card dashboard-chart-doughnut">
            <h3>Status mix</h3>
            <div className="dashboard-chart-inner">
              {!loading && sums.total > 0 ? (
                <Doughnut data={statusDoughnutData} options={doughnutStatusOptions} />
              ) : (
                <p className="dashboard-no-data" style={{ paddingTop: 48 }}>
                  {loading ? 'Loading chart…' : 'No messages for this period.'}
                </p>
              )}
            </div>
          </div>
          <div className="dashboard-chart-card dashboard-chart-line">
            <h3>Breakdown by status</h3>
            <div className="dashboard-chart-inner" style={{ minHeight: 220 }}>
              {!loading && sums.total > 0 ? (
                <Bar data={statusBarData} options={barOptionsVertical} />
              ) : (
                <p className="dashboard-no-data" style={{ paddingTop: 48 }}>
                  {loading ? 'Loading chart…' : 'No data.'}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <section className="admin-panel">
        <div className="admin-panel__head">
          <h2>Message stats</h2>
          <p>{loading ? 'Loading…' : sums.total === 0 ? 'No messages for this period' : 'Your account summary'}</p>
        </div>
        <div className="admin-panel__body" style={{ padding: 0 }}>
          <div className="admin-scroll">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>User ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Total</th>
                  <th>Read</th>
                  <th>Sent</th>
                  <th>Delivered</th>
                  <th>Failed</th>
                  <th>Pending</th>
                  <th>Delivery failed</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="10" className="admin-empty">
                      Loading stats…
                    </td>
                  </tr>
                ) : sums.total === 0 ? (
                  <tr>
                    <td colSpan="10" className="admin-empty">
                      No messages for the selected period.
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td>{stats?.user_id ?? '—'}</td>
                    <td>{profile.name || '—'}</td>
                    <td>{profile.email || '—'}</td>
                    <td>{formatInt(sums.total)}</td>
                    <td>{formatInt(sums.read)}</td>
                    <td>{formatInt(sums.sent)}</td>
                    <td>{formatInt(sums.delivered)}</td>
                    <td>{formatInt(sums.failed)}</td>
                    <td>{formatInt(sums.pending)}</td>
                    <td>{formatInt(sums.delivery_failed)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
};

export default UserOverview;
